import React, { useState, useEffect } from 'react'
import { shallow } from 'zustand/shallow'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import Box from '@mui/material/Box'
import { SelectChangeEvent } from '@mui/material'

import { useGetLoginInfo } from '@multiversx/sdk-dapp/hooks/account'
import { useTrackTransactionStatus } from '@multiversx/sdk-dapp/hooks/transactions/useTrackTransactionStatus'

import { ZoidCore } from '@zoidpay-tech/zoidcore-web3-toolkit'
import { NftCreate } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models/nft/nftCreate'
import { GetCollectionInfo } from '@zoidpay-tech/zoidcore-web3-toolkit/build/models'

import { useChainAndNetwork } from 'src/store/chainAndNetwork'
import { mvxNetworkProvidersType } from '../../util/types'
import useBlockchainAdapter from '../../hooks/useBlockchainAdapter'
import { useAuthState, useAuthDispatch } from '../../contexts/authContext'
import { getNftCollections } from '../../contexts/blockchainActionCreators'

import ConnectMvxInfo from '../../components/ConnectMvxInfo'
import InfoPaper from '../../components/InfoPaper'
import SectionTitle from '../../components/kit/SectionTitle'
import SettingTitle from '../../components/kit/SettingTitle'
import BoxInputField from '../../components/kit/BoxInputField'
import FormActionButton from '../../components/kit/FormActionButton'
import NftCollectionSelect from '../../components/NftCollectionSelect'

const EsdtForm = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const identifier = queryParams.get('tokenIdentifier')
  const name = queryParams.get('nftName')
  const queryRoyalties = parseInt(queryParams.get('royalties') ?? '0')
  const queryUriLink = queryParams.get('uriLink')
  const queryIpfsCid = queryParams.get('ipfsCid')
  const queryFileName = queryParams.get('fileName')

  const [address, setAddress] = useState<string>('')
  const [tokenIdentifier, setTokenIdentifier] = useState<string>(identifier ?? '')
  const [nftName, setNftName] = useState<string>(name ?? '')
  const [royalties, setRoyalties] = useState<number>(queryRoyalties || 5)
  const [uriLink, setUriLink] = useState<string>(queryUriLink ?? '')
  const [ipfsCid, setIpfsCid] = useState<string>(queryIpfsCid ?? '')
  const [fileName, setFileName] = useState<string>(queryFileName ?? '')
  const [disabled, setDisabled] = useState<boolean>(false)
  const [nftCollections, setNftCollections] = useState<any[]>([])
  const [collectionInfo, setCollectionInfo] = useState<any>({})

  let url = new URL(window.location.href)
  const adapter = useBlockchainAdapter()
  const { network } = useChainAndNetwork(
    (state) => ({
      network: state.network,
    }),
    shallow,
  )

  const { isLoggedIn } = useGetLoginInfo()
  const navigate = useNavigate()

  const mvxNetworkProviders: mvxNetworkProvidersType = {
    testnet: 'https://devnet-api.multiversx.com',
    mainnet: 'https://api.multiversx.com',
  }

  useEffect(() => {
    const setAddressAfterLogin = async () => {
      const walletAddress = await adapter.multiversx?.getAddress()
      setAddress(walletAddress)
    }
    if (isLoggedIn && adapter.multiversx) {
      setAddressAfterLogin()
    }
  }, [isLoggedIn, adapter.multiversx])

  const user = useAuthState().user
  const dispatchAction = useAuthDispatch()

  const initializer = {
    apiKey: '',
    authorization: user?.accessToken,
    env: process.env.REACT_APP_ENVIRONMENT === 'development' ? 'debug' : '',
  }
  const zoid = new ZoidCore(initializer)

  const nftCreatePayload: NftCreate = {
    address,
    tokenIdentifier,
    nftName,
    initialQuantity: 1,
    royalties: royalties * 100,
    addressToSendTo: address,
    URILinks: [uriLink],
    senderAddress: address,
    hash: ipfsCid,
    attributes: `metadata:${ipfsCid}/${fileName}}`,
    networkProvider: mvxNetworkProviders[network],
  }

  const handleNftNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    url.searchParams.set('nftName', e.target.value)
    window.history.pushState({}, '', url)
    setNftName(e.target.value)
  }

  const handleTokenIdentifierChange = (e: SelectChangeEvent<string>) => {
    url.searchParams.set('tokenIdentifier', e.target.value)
    window.history.pushState({}, '', url)
    setTokenIdentifier(e.target.value)
  }

  const handleRoyaltiesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    url.searchParams.set('royalties', `${parseInt(e.target.value) || 0}`)
    window.history.pushState({}, '', url)
    setRoyalties(parseInt(e.target.value) || 0)
  }

  const handleUriLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    url.searchParams.set('uriLink', e.target.value)
    window.history.pushState({}, '', url)
    setUriLink(e.target.value)
  }

  const handleIpfsCidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    url.searchParams.set('ipfsCid', e.target.value)
    window.history.pushState({}, '', url)
    setIpfsCid(e.target.value)
  }

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    url.searchParams.set('fileName', e.target.value)
    window.history.pushState({}, '', url)
    setFileName(e.target.value)
  }

  const sessionPayload = sessionStorage.getItem('mvxMintNftPayload')
  const sessionPayloadParsed = JSON.parse(sessionPayload || '{}')

  const transactionStatus = useTrackTransactionStatus({
    transactionId: sessionPayloadParsed?.txId,
    onSuccess: async () => {
      navigate('/projects')
      setDisabled(false)
    },
    onCancelled: () => {
      setDisabled(false)
    },
    onFail() {
      setDisabled(false)
    },
  })

  useEffect(() => {
    if (transactionStatus?.isPending) {
      setDisabled(true)
    }
  }, [transactionStatus?.isPending])

  useEffect(() => {
    dispatchAction({
      type: 'DISABLENETWORKOPTIONS',
      payload: disabled,
    })
  }, [disabled])

  const handleOpenCollectionSelect = async () => {
    if (address) {
      const collections = await getNftCollections(address, network)
      setNftCollections(collections)
    }
  }

  useEffect(() => {
    const fetchNftCollections = async () => {
      if (identifier && address) {
        const collections = await getNftCollections(address, network)
        setNftCollections(collections)
        setTokenIdentifier(identifier)
      }
    }
    fetchNftCollections()
  }, [identifier, address])

  useEffect(() => {
    const getCollectionInfo = async () => {
      const collectionInfoPayload: GetCollectionInfo = {
        address,
        collectionIdentifier: tokenIdentifier,
        env: network,
      }
      const collectionInfo =
        await zoid.multiversX.getCollectionInfoMultiversX(collectionInfoPayload)
      setCollectionInfo(collectionInfo?.data?.data)
    }
    if (tokenIdentifier && address) {
      getCollectionInfo()
    }
  }, [tokenIdentifier, address])

  const handleNavigateSetRoles = () => {
    navigate(
      `/nft-set-role?chain=MULTIVERSX&network=${network}&tokenIdentifier=${tokenIdentifier}&addressToAssignRole=${address}`,
    )
  }

  const checkFieldRules = (string: string): boolean => {
    const regex = /^\S+(?:\s+\S+)*$/
    return regex.test(string)
  }

  const checkRoyaltiesRules = (): boolean => {
    return royalties >= 0 && royalties <= 100
  }

  const handleCreateNft = async () => {
    if (address && collectionInfo?.role?.canCreate === false) {
      return navigate(
        `/nft-set-role?chain=MULTIVERSX&network=${network}&tokenIdentifier=${tokenIdentifier}&addressToAssignRole=${address}`,
      )
    }

    setDisabled(true)

    try {
      sessionStorage.setItem(
        'mvxMintNftPayload',
        JSON.stringify({
          ...nftCreatePayload,
          chain: 'multiversx',
          network,
          operation: 'mint NFT',
        }),
      )
      const response = await zoid.multiversX.nftCreate(nftCreatePayload)
      const { transaction, transactionData } = response?.data?.data || {}

      const createNftTransaction = {
        data: transactionData.data,
        sender: transactionData.sender.bech32,
        receiver: transactionData.receiver.bech32,
        value: transaction.value,
        gasLimit: transaction.gasLimit + transactionData.data.length * 1500,
      }

      const transactionRes = await adapter.multiversx.sendTransaction({
        transactions: createNftTransaction,
        transactionsDisplayInfo: {
          processingMessage: 'Processing Nft mint transaction',
          errorMessage: 'An error has occurred during transaction',
          successMessage: 'Nft mint transaction successful',
        },
        redirectAfterSign: false,
      })
      const payload = sessionStorage.getItem('mvxMintNftPayload')
      const parsedPayload = JSON.parse(payload as string)
      sessionStorage.setItem(
        'mvxMintNftPayload',
        JSON.stringify({
          ...parsedPayload,
          txId: transactionRes?.sessionId,
        }),
      )
    } catch (error) {
      console.error('error', error)
      setDisabled(false)
      if (error instanceof Error) {
        toast.error(error?.message, {
          progressClassName: 'simpleBar',
        })
      }
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '20px',
          width: '100%',
        }}>
        <ConnectMvxInfo infoText="You have to connect your wallet before minting a NFT." />
      </Box>
      <SectionTitle title="Settings" />
      <BoxInputField
        title="Name"
        alignTitleCenter
        placeholder="NFT name"
        value={nftName}
        error={!checkFieldRules(nftName) && nftName.length > 0}
        onChange={handleNftNameChange}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '10px',
          width: '100%',
        }}>
        <SettingTitle title="Symbol" alignCenter />
        <NftCollectionSelect
          nftCollections={nftCollections}
          selectedNftCollection={tokenIdentifier}
          handleNftCollectionChange={handleTokenIdentifierChange}
          onOpen={handleOpenCollectionSelect}
          sx={{
            '& .MuiFormLabel-root': {
              color: 'rgba(255, 255, 255, 0.5)',
              fontFamily: 'SpaceGrotesk, sans-serif',
            },
            '.MuiSvgIcon-root': {
              '&.MuiSelect-icon': {
                color: 'rgba(255, 255, 255, 0.5)',
              },
            },
            '& .MuiSelect-outlined': {
              borderColor: 'transparent',
              color: '#fff',
              background: 'rgba(255, 255, 255, 0.05)',
              fontFamily: 'SpaceGrotesk, sans-serif',
            },
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '20px',
          width: '100%',
        }}>
        {address && collectionInfo?.role?.canCreate === false ? (
          <InfoPaper
            showButton
            buttonText="Set roles"
            buttonAction={handleNavigateSetRoles}
            infoText="Your address does not have the 'ESDTRoleNFTCreate' to be able to mint a new NFT. You first need to asign this role to your address."
          />
        ) : null}
      </Box>
      {/* <BoxInputField
        title="Symbol"
        alignTitleCenter
        placeholder='e.g. "NCT"'
        value={tokenIdentifier}
        onChange={handleTokenIdentifierChange}
        helperText="Alphanumeric uppercase characters only, between 3 and 10 characters"
      /> */}
      <BoxInputField
        title="Royalties"
        alignTitleCenter
        placeholder='e.g. "75"'
        value={royalties}
        onChange={handleRoyaltiesChange}
        error={!checkRoyaltiesRules()}
        helperText="Between 0 and 100%"
      />
      <BoxInputField
        title="Uri link"
        alignTitleCenter
        placeholder='e.g. "https://ipfs.io/..."'
        value={uriLink}
        onChange={handleUriLinkChange}
        error={!checkFieldRules(uriLink) && uriLink.length > 0}
      />
      <BoxInputField
        title="Ipfs CID"
        alignTitleCenter
        placeholder='e.g. "QmXxoinT..."'
        value={ipfsCid}
        onChange={handleIpfsCidChange}
        error={!checkFieldRules(ipfsCid) && ipfsCid.length > 0}
      />
      <BoxInputField
        title="File name"
        alignTitleCenter
        placeholder='e.g. "file-name.gif"'
        value={fileName}
        onChange={handleFileNameChange}
        error={!checkFieldRules(fileName) && fileName.length > 0}
      />
      <FormActionButton
        text="Mint NFT"
        onClick={handleCreateNft}
        disabledState={disabled}
        disabled={
          disabled ||
          !address ||
          !checkFieldRules(nftName) ||
          !tokenIdentifier ||
          !checkRoyaltiesRules() ||
          !checkFieldRules(uriLink) ||
          !checkFieldRules(ipfsCid) ||
          !checkFieldRules(fileName)
        }
      />
    </>
  )
}

export default EsdtForm
